import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import numeral from 'numeral';
import queryString from 'query-string';
import find from 'lodash/find';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';

import _ from '@ihme/common/locale';
import styled from '@ihme/common/theme/styled';
import {
    ChartProvider,
    ChartDropdown,
    ChartHierarchicalDropdown,
    Clearfix,
    Col,
    Form,
    LineChart,
    PageHeader,
} from '@ihme/common/web/components';

import { loadExtendedCauses, loadTrendsFilters } from '../../actions/data-actions';
import { getTrendsFilters, getExtendedCauses } from '../../reducers';
import { prepareCauseFilters } from './data-helpers';
import api from '../../api';
import config from '../../config';
import locale from './locale';
import echartsTheme from '../../theme/echartsTheme';
import { pngIcon, csvIcon } from '../../theme/icons';
import { sortLocationOptions } from '../../utility/sorting-helpers';

const ButtonGroup = styled('div')(({ theme }) => ({
    '> *': {
        width: 124,
        marginRight: 12,
        [theme.breakpoint.xs]: {
            width: 252,
        },
    },
}));

const CausesDropdown = styled(ChartHierarchicalDropdown)({
    width: 248,
});

const LocationsDropdown = styled(ChartDropdown)({
    minWidth: 186,
});

const DEFAULT_FILTERS = {
    age_group_id: config.defaultAgeGroupId,
    gender_id: config.trendsDefaultGenderId,
    measure_id: config.defaultMeasureId,
    metric_id: config.numberMetricId,
    cause_id: config.defaultCauseId,
    location_id: config.defaultLocationId,
};

type TrendFilters = {
    age_group_id: number;
    gender_id: string | number | Array<string | number>;
    measure_id: number;
    metric_id: number;
    cause_id: number;
    location_id: number;
    [key: string]: unknown;
};

type Props = {
    history: { [key: string]: unknown };
    location: { search: string; [key: string]: unknown };
    filters?: TrendFilters[] | null;
    extendedCauses?: Array<Record<string, unknown>> | null;
    loadFilters: () => void;
    loadExtendedCauses: () => void;
};

const TrendsScene = ({
    filters,
    extendedCauses,
    history,
    location,
    loadFilters,
    loadExtendedCauses,
}: Props) => {
    const [selectedFilters, setSelectedFilters] = useState<Record<string, any>>({});
    const [availableMeasures, setAvailableMeasures] = useState<Array<number | string>>([]);

    useEffect(() => {
        if (filters == null) {
            loadFilters();
        }
        if (extendedCauses == null) {
            loadExtendedCauses();
        }
    }, [filters, extendedCauses, loadFilters, loadExtendedCauses]);

    const getInitialFilters = useCallback(
        () => queryString.parse(location.search),
        [location.search]
    );

    const getYAxisKeys = useCallback(
        () =>
            selectedFilters && selectedFilters.gender_id
                ? selectedFilters.gender_id
                      .toString()
                      .split(',')
                      .map((genderId: string) => `gender_${genderId}_value`)
                : [],
        [selectedFilters]
    );

    const renderTooltip = useCallback(
        (params: any[], { isMobileView }: { isMobileView: boolean }) => {
            const { measure_id, cause_id } = selectedFilters;
            const tooltipFontSize = isMobileView ? 10 : 14;

            return (
                `<div style="text-align:left;font-size:${tooltipFontSize}px">` +
                params
                    .map(obj => {
                        const valueIdx = obj?.encode?.y[0] || 0;
                        const value = obj?.data?.[valueIdx];
                        return (
                            obj.marker +
                            ' ' +
                            _(`trends_tooltip_measure_${measure_id}`, {
                                gender: obj.seriesName,
                                value: `<b>${numeral(value).format('0,0.0')}</b>`,
                                cause: _(`cause_${cause_id}`),
                                year: obj.axisValue,
                                separator: '<br/>&nbsp;&nbsp;&nbsp;&nbsp;',
                            })
                        );
                    })
                    .join('<br/>') +
                '</div>'
            );
        },
        [selectedFilters]
    );

    const getLineParams = useCallback(
        () =>
            selectedFilters && selectedFilters.gender_id
                ? selectedFilters.gender_id
                      .toString()
                      .split(',')
                      .map((genderId: string, i: number) => ({
                          name: _(`gender_${genderId}`),
                          color: echartsTheme.line.colors[i],
                      }))
                : [],
        [selectedFilters]
    );

    const getSaveFilename = useCallback(
        ({ filters: selected, yearsRange }: any) =>
            'Trends, ' +
            _(`cause_${selected.cause_id}`) +
            ' ' +
            _(`measure_${selected.measure_id}`) +
            ' ' +
            _(`metric_${selected.metric_id}`) +
            ' in ' +
            _(`location_${selected.location_id}`) +
            ', ' +
            _(`age_group_${selected.age_group_id}`) +
            ', ' +
            _(`gender_${selected.gender_id}`) +
            ', ' +
            yearsRange.range,
        []
    );

    const loadData = useCallback(
        (requestFilters: Record<string, unknown>) =>
            new Promise((resolve, reject) =>
                api.data
                    .getCauseOutcome(requestFilters)
                    .then((response: any) => {
                        let { columns, records } = response;
                        if (records.length === 0) {
                            resolve({ columns, records });
                            return;
                        }

                        const genderIndex = columns.indexOf('gender_id');
                        const valueIndex = columns.indexOf('value');
                        const yearIndex = columns.indexOf('year');
                        const genderIds = uniq(records.map((record: any[]) => record[genderIndex]));
                        const dataByGender = groupBy(
                            records,
                            (record: any[]) => record[genderIndex]
                        );
                        const reference = dataByGender[genderIds[0]];

                        columns.push(...genderIds.map(genderId => `gender_${genderId}_value`));
                        records = sortBy(reference, yearIndex).map((record: any[]) => [
                            ...record,
                            ...genderIds.map(
                                genderId =>
                                    find(
                                        dataByGender[genderId],
                                        genderRecord =>
                                            genderRecord[yearIndex] === record[yearIndex]
                                    )[valueIndex]
                            ),
                        ]);

                        resolve({ columns, records });
                    })
                    .catch(reject)
            ),
        []
    );

    const onFiltersChange = useCallback(
        (updatedFilters: unknown, nextFilters: Record<string, any>) => {
            setSelectedFilters(nextFilters);
            setAvailableMeasures([]);

            const criteria = {
                filters: 'measure_id',
                cause_id: nextFilters.cause_id,
            };

            api.data
                .getCauseOutcomeFilters(criteria)
                .then((response: any) => setAvailableMeasures(response.measure_id))
                .catch(console.log);
        },
        []
    );

    const preprocessMeasures = useCallback(
        (measures: Array<{ value: number | string }>) =>
            measures.map(measure => ({
                ...measure,
                isDisabled: availableMeasures.indexOf(measure.value) === -1,
            })),
        [availableMeasures]
    );

    const preparedFilters = useMemo(() => prepareCauseFilters(filters as any), [filters]);

    return (
        <>
            <PageHeader>{_(locale.title)}</PageHeader>
            <Col xs={12} md={9} mdPush={3}>
                <ChartProvider
                    key={String(preparedFilters)}
                    loadFilters={() => Promise.resolve(preparedFilters)}
                    loadData={loadData}
                    defaultFilters={DEFAULT_FILTERS}
                    initialFilters={getInitialFilters()}
                    onFiltersChange={onFiltersChange}
                    history={history}
                    location={location}
                >
                    <Form inline>
                        <ButtonGroup>
                            <ChartDropdown
                                label={_(locale.ageGroupsTitle)}
                                localePrefix="age_group_"
                                filterKey="age_group_id"
                            />
                            <ChartDropdown
                                label={_(locale.gendersTitle)}
                                localePrefix="gender_"
                                filterKey="gender_id"
                            />
                            <ChartDropdown
                                label={_(locale.measuresTitle)}
                                localePrefix="measure_"
                                filterKey="measure_id"
                                preprocessOptions={preprocessMeasures}
                            />
                            <ChartDropdown
                                label={_(locale.metricsTitle)}
                                localePrefix="metric_"
                                filterKey="metric_id"
                            />
                            <CausesDropdown
                                key={String(extendedCauses)}
                                loadHierarchy={() => Promise.resolve(extendedCauses)}
                                label={_(locale.causesTitle)}
                                localePrefix="cause_"
                                filterKey="cause_id"
                                isSearchable
                                rootLevel={0}
                            />
                            <LocationsDropdown
                                label={_(locale.locationsTitle)}
                                localePrefix="location_"
                                filterKey="location_id"
                                preprocessOptions={sortLocationOptions}
                                isSearchable
                            />
                        </ButtonGroup>
                        <Clearfix />
                    </Form>

                    <LineChart
                        theme={echartsTheme}
                        withSlider
                        saveAsImage={{
                            visible: true,
                            filename: getSaveFilename,
                            icon: pngIcon,
                        }}
                        saveAsCSV={{
                            visible: true,
                            filename: getSaveFilename,
                            icon: csvIcon,
                            headers: [
                                'Location',
                                'Year',
                                'Unit',
                                'Measure',
                                'Cause',
                                'Age',
                                'Gender',
                                'Value',
                            ],
                            keys: [
                                'location_id',
                                'year',
                                'metric_id',
                                'measure_id',
                                'cause_id',
                                'age_group_id',
                                'gender_id',
                                ...getYAxisKeys(),
                            ],
                        }}
                        yAxisKeys={getYAxisKeys()}
                        lineParams={getLineParams()}
                        renderTooltip={renderTooltip}
                        renderTitle={({ filters: selected, yearsRange }: any) =>
                            _(`cause_${selected.cause_id}`) +
                            ' ' +
                            _(`measure_${selected.measure_id}`) +
                            ' ' +
                            _(`metric_${selected.metric_id}`) +
                            ' in ' +
                            _(`location_${selected.location_id}`) +
                            ' ' +
                            yearsRange.range
                        }
                        renderSubtitle={({ filters: selected }: any) =>
                            _(`age_group_${selected.age_group_id}`)
                        }
                        formatSeriesName={(key: string) => {
                            const suffix = '_value';
                            const genderKey = key.endsWith(suffix)
                                ? key.slice(0, -suffix.length)
                                : key;
                            return _(genderKey);
                        }}
                    />
                </ChartProvider>
            </Col>
            <Col xs={12} md={3} mdPull={9}>
                <p dangerouslySetInnerHTML={{ __html: _(locale.highlightText) }} />
            </Col>
        </>
    );
};

const mapStateToProps = (state: any) => ({
    filters: getTrendsFilters(state),
    extendedCauses: getExtendedCauses(state),
});

export default compose(
    withRouter,
    connect(mapStateToProps, {
        loadExtendedCauses,
        loadFilters: loadTrendsFilters,
    })
)(TrendsScene);
